import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Backdrop,
  CircularProgress,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
} from "@mui/material";
import MDBox from "components/MDBox";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import { useGetPlansQuery, useUpdatePlanMutation } from "../../redux/Slices/plans";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

const getPlanIcon = (adsSystem) => {
  switch (adsSystem) {
    case "basic_ads":
      return "🛡️";
    case "standard_ads":
      return "📈";
    case "advanced_ads":
      return "🚀";
    case "full_ads":
      return "👑";
    default:
      return "🛡️";
  }
};

const StyledCard = styled(Card)(({ theme }) => ({
  position: "relative",
  borderRadius: "12px",
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 60%, ${theme.palette.primary.light}20)`,
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  padding: theme.spacing(2),
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-8px)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "4px",
    background: `linear-gradient(to right, #ffdf6f, #dcb060 )`,
  },
}));

const GradientTypography = styled(Typography)(({ theme }) => ({
  background: "linear-gradient(to right, #ffdf6f, #dcb060)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  textFillColor: "transparent",
}));

const PlansScreen = () => {
  const [editPlanOpen, setEditPlanOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [formData, setFormData] = useState({
    product_limit: "",
    offers_limit: "",
    ads_limit: "",
    orders_limit: "",
    price: "",
    billing_cycle: "monthly",
    trial_days: "",
    users_limit: "",
    is_active: true,
  });
  const [formErrors, setFormErrors] = useState({});

  const { data, error, isLoading, refetch } = useGetPlansQuery();
  const [updatePlan, { isLoading: isUpdating, error: updateError }] = useUpdatePlanMutation();

  const handleToggle = (planId) => {
    setExpanded((prev) => ({
      ...prev,
      [planId]: !prev[planId],
    }));
  };

  const handleEditClick = (plan) => {
    setSelectedPlan(plan);
    setFormData({
      product_limit: plan.product_limit || "",
      offers_limit: plan.offers_limit || "",
      ads_limit: plan.ads_limit || "",
      orders_limit: plan.orders_limit || "",
      price: plan.price || "",
      billing_cycle: plan.billing_cycle || "monthly",
      trial_days: plan.trial_days || "",
      users_limit: plan.users_limit || "",
      is_active: plan.is_active !== undefined ? Boolean(plan.is_active) : true,
    });
    setFormErrors({});
    setEditPlanOpen(true);
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.product_limit || formData.product_limit <= 0) {
      errors.product_limit = "حد المنتجات مطلوب ويجب أن يكون أكبر من صفر";
    }
    if (!formData.offers_limit || formData.offers_limit <= 0) {
      errors.offers_limit = "حد العروض مطلوب ويجب أن يكون أكبر من صفر";
    }
    if (!formData.ads_limit || formData.ads_limit <= 0) {
      errors.ads_limit = "حد الإعلانات مطلوب ويجب أن يكون أكبر من صفر";
    }
    if (!formData.orders_limit || formData.orders_limit <= 0) {
      errors.orders_limit = "حد الطلبات مطلوب ويجب أن يكون أكبر من صفر";
    }
    if (!formData.price || formData.price <= 0) {
      errors.price = "السعر مطلوب ويجب أن يكون أكبر من صفر";
    }
    if (!formData.trial_days || formData.trial_days < 0) {
      errors.trial_days = "أيام التجربة مطلوبة ويجب أن تكون صفر أو أكثر";
    }
    if (!formData.users_limit || formData.users_limit <= 0) {
      errors.users_limit = "حد المستخدمين مطلوب ويجب أن يكون أكبر من صفر";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  // create and update plan  handlers
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const updateData = {
        ...formData,
        product_limit: parseInt(formData.product_limit),
        offers_limit: parseInt(formData.offers_limit),
        ads_limit: parseInt(formData.ads_limit),
        orders_limit: parseInt(formData.orders_limit),
        price: parseFloat(formData.price),
        trial_days: formData.trial_days.toString(),
        users_limit: parseInt(formData.users_limit),
        is_active: formData.is_active ? 1 : 0,
      };
      await updatePlan(selectedPlan.id, updateData).unwrap();
      // Close dialog and refresh data
      setEditPlanOpen(false);
      setSelectedPlan(null);
      refetch();
    } catch (error) {
      console.error("Error updating plan:", error);
    }
  };

  const handleCloseDialog = () => {
    setEditPlanOpen(false);
    setSelectedPlan(null);
    setFormData({
      product_limit: "",
      offers_limit: "",
      ads_limit: "",
      orders_limit: "",
      price: "",
      billing_cycle: "monthly",
      trial_days: "",
      users_limit: "",
      is_active: true,
    });
    setFormErrors({});
  };

  if (isLoading) {
    return (
      <>
        <Backdrop open={isLoading} sx={{ zIndex: 2000, color: "#fff" }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </>
    );
  }

  if (error) {
    return <Typography color="error">حدث خطأ أثناء تحميل الخطط</Typography>;
  }

  const plans = data?.data || [];

  return (
    <>
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            mb={3}
            py={3}
            px={2}
            borderRadius="lg"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              background: "linear-gradient(90deg, #ca9937, #e0b15a)",
              boxShadow: 3,
            }}
          >
            <MDTypography variant="h6" color="white">
              الخطط والاشتركات
            </MDTypography>

            <MDButton
              variant="contained"
              color="white"
              startIcon={<AddIcon />}
              sx={{ color: "white" }}
              onClick={() => console.log("Create plan clicked")}
            >
              انشاء خطة
            </MDButton>
          </MDBox>

          <Box container spacing={3} sx={{ display: "flex", flexWrap: "wrap" }}>
            {plans.map((plan) => (
              <Box sx={{ width: "calc(35% - 20px)", margin: "10px" }} key={plan.id}>
                <StyledCard>
                  <CardContent>
                    <Box display="flex" justifyContent="center" alignItems="center">
                      <Typography variant="h3" align="center" color="primary" gutterBottom>
                        {getPlanIcon(plan.ads_system)}
                      </Typography>
                      <Typography variant="h5" align="center" fontWeight="bold" gutterBottom>
                        {plan.name}
                      </Typography>
                    </Box>
                    <GradientTypography variant="h6" align="center" gutterBottom>
                      {plan.price} شهرياً
                    </GradientTypography>
                    <List dense>
                      <ListItem>
                        <ListItemText primary={`حد المنتجات: ${plan.product_limit}`} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary={`حد الإعلانات: ${plan.ads_limit}`} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary={`حد الطلبات: ${plan.orders_limit}`} />
                      </ListItem>
                      <Collapse in={expanded[plan.id]} timeout="auto" unmountOnExit>
                        <List dense>
                          <ListItem>
                            <ListItemText primary={`نظام العروض: ${plan.offers_system}`} />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary={`نظام الإعلانات: ${plan.ads_system}`} />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary={`شركات الشحن: ${
                                plan.shipping_companies?.join(", ") || "غير محدد"
                              }`}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary={`بوابات الدفع: ${
                                plan.payment_gateways?.join(", ") || "غير محدد"
                              }`}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary={`عمولة المبيعات: ${plan.sales_commission}`} />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary={`الدعم الفني: ${
                                plan.technical_support?.join(", ") || "غير محدد"
                              }`}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary={`أيام التجربة: ${plan.trial_days}`} />
                          </ListItem>
                          <ListItem>
                            <ListItemText primary={`حد المستخدمين: ${plan.users_limit}`} />
                          </ListItem>
                        </List>
                      </Collapse>
                    </List>
                    <Box display="flex" justifyContent="center" mt={2}>
                      <IconButton
                        onClick={() => handleToggle(plan.id)}
                        endIcon={expanded[plan.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      >
                        {expanded[plan.id] ? <RemoveRedEyeIcon /> : <VisibilityOffIcon />}
                      </IconButton>
                      <IconButton onClick={() => handleEditClick(plan)}>
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Box>
            ))}
          </Box>
        </Card>
      </MDBox>

      {/* Edit Plan Dialog */}
      <Dialog open={editPlanOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth dir="rtl">
        <DialogTitle
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Typography variant="h6">تعديل الخطة: {selectedPlan?.name}</Typography>
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {updateError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              حدث خطأ أثناء تحديث الخطة
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="product_limit"
                label="حد المنتجات"
                type="number"
                fullWidth
                value={formData.product_limit}
                onChange={handleInputChange}
                error={!!formErrors.product_limit}
                helperText={formErrors.product_limit}
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="offers_limit"
                label="حد العروض"
                type="number"
                fullWidth
                value={formData.offers_limit}
                onChange={handleInputChange}
                error={!!formErrors.offers_limit}
                helperText={formErrors.offers_limit}
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="ads_limit"
                label="حد الإعلانات"
                type="number"
                fullWidth
                value={formData.ads_limit}
                onChange={handleInputChange}
                error={!!formErrors.ads_limit}
                helperText={formErrors.ads_limit}
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="orders_limit"
                label="حد الطلبات"
                type="number"
                fullWidth
                value={formData.orders_limit}
                onChange={handleInputChange}
                error={!!formErrors.orders_limit}
                helperText={formErrors.orders_limit}
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="price"
                label="السعر"
                type="number"
                fullWidth
                value={formData.price}
                onChange={handleInputChange}
                error={!!formErrors.price}
                helperText={formErrors.price}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="medium">
                <InputLabel id="billing-cycle-label">دورة الفواتير</InputLabel>
                <Select
                  labelId="billing-cycle-label"
                  name="billing_cycle"
                  value={formData.billing_cycle}
                  onChange={handleInputChange}
                  label="دورة الفواتير"
                  sx={{
                    "& .MuiSelect-select": {
                      padding: "16.5px 14px",
                      minHeight: "1.4375em",
                    },
                  }}
                >
                  <MenuItem value="monthly">شهرياً</MenuItem>
                  <MenuItem value="yearly">سنوياً</MenuItem>
                  <MenuItem value="weekly">أسبوعياً</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="trial_days"
                label="أيام التجربة"
                type="number"
                fullWidth
                value={formData.trial_days}
                onChange={handleInputChange}
                error={!!formErrors.trial_days}
                helperText={formErrors.trial_days}
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="users_limit"
                label="حد المستخدمين"
                type="number"
                fullWidth
                value={formData.users_limit}
                onChange={handleInputChange}
                error={!!formErrors.users_limit}
                helperText={formErrors.users_limit}
                inputProps={{ min: 1 }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    color="primary"
                  />
                }
                label="الخطة نشطة"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={handleCloseDialog}>
            إلغاء
          </MDButton>
          <MDButton
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isUpdating}
          >
            {isUpdating ? <CircularProgress size={20} /> : "تحديث الخطة"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PlansScreen;
